import Slide from '@/components/shared/Slide';
import {
  getLayerLabel,
  getLayerDescription,
  getLayerAccentClass,
  getLayerOrder,
} from '@/lib/investment-layers';
import {
  REALISTIC_FUNDING_DECK_FX,
  awardAmountMaxToUsd,
  getLayerRealisticFundingUsdSummed,
} from '@/lib/realistic-funding';
import { cn } from '@/lib/utils';
import {
  ACTION_TIER_LABELS,
  ACTION_TIER_ORDER,
  ActionChip,
  type ActionTier,
  goNoGoToActionTier,
} from '@/components/shared/Chip';
import type {
  GrantProgram,
  InvestmentLayer,
  ProgramLayerId,
  ViewMode,
  PriorityProgramOverride,
} from '@/types/roadmap.types';

function formatFunding(programs: GrantProgram[]): string {
  const total = programs.reduce((sum, p) => sum + awardAmountMaxToUsd(p), 0);
  if (total >= 1_000_000) return `$${(total / 1_000_000).toFixed(1)}M`;
  if (total >= 1_000) return `$${(total / 1_000).toFixed(0)}K`;
  if (total > 0) return `$${total}`;
  return 'Varies';
}

interface Props {
  programs: GrantProgram[];
  mode: ViewMode;
  investmentLayers?: InvestmentLayer[];
  priorityOverrides?: PriorityProgramOverride[];
}

export default function LayerGroupSlide({ programs, mode, investmentLayers, priorityOverrides }: Props) {
  const layerOrder = getLayerOrder(investmentLayers);
  const hasCopPrograms = programs.some((p) => p.currency?.toUpperCase() === 'COP');

  const programsByLayer = new Map<ProgramLayerId, GrantProgram[]>();
  for (const p of programs) {
    const key = p.investment_layer;
    if (!programsByLayer.has(key)) programsByLayer.set(key, []);
    programsByLayer.get(key)!.push(p);
  }

  const groups = layerOrder
    .filter((layer) => programsByLayer.has(layer))
    .map((layer) => ({
      layer,
      label: getLayerLabel(layer, investmentLayers),
      desc: getLayerDescription(layer, investmentLayers),
      accent: getLayerAccentClass(layer),
      programs: programsByLayer.get(layer)!,
    }));

  return (
    <Slide mode={mode} wide>
      <div className="w-full max-w-7xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Investment View
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-8 print:text-[16pt] print:mb-4">
          Funding by Objective
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 print:grid-cols-2 print:gap-3">
          {groups.map((g) => {
            const tierCounts: Record<ActionTier, number> = {
              primary: 0,
              go: 0,
              conditional: 0,
              monitor: 0,
            };
            for (const p of g.programs) {
              const tier = goNoGoToActionTier(p.go_no_go);
              if (tier) tierCounts[tier]++;
            }
            const decisions = ACTION_TIER_ORDER
              .map((tier) => ({ tier, count: tierCounts[tier], label: ACTION_TIER_LABELS[tier] }))
              .filter((d) => d.count > 0);
            const realisticUsd = getLayerRealisticFundingUsdSummed(g.programs, priorityOverrides);

            return (
              <div
                key={g.layer}
                className={cn(
                  'rounded-xl border border-border/50 border-t-4 p-6 print:p-3 print:rounded-lg flex flex-col justify-between',
                  g.accent,
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-foreground print:text-[12pt]">
                      {g.label}
                    </h3>
                    {g.desc && (
                      <p className="text-xs text-muted-foreground mt-0.5 print:text-[8pt]">
                        {g.desc}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-3xl font-bold text-foreground tabular-nums print:text-[14pt]">
                      {g.programs.length}
                    </span>
                    <p className="text-[11px] text-muted-foreground -mt-0.5 print:text-[8pt]">
                      {g.programs.length === 1 ? 'program' : 'programs'}
                    </p>
                  </div>
                </div>

                <div className="mt-5 flex items-end justify-between gap-4 print:mt-3">
                  <div className="min-w-0 flex-1 pr-2">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium print:text-[7pt]">
                      Funding Potential
                    </p>
                    <p className="text-xl font-bold text-foreground tabular-nums mt-0.5 print:text-[11pt]">
                      {formatFunding(g.programs)}
                    </p>
                    {realisticUsd && (
                      <div className="mt-3 print:mt-1.5">
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium print:text-[7pt]">
                          Realistic funding (est.)
                        </p>
                        <p className="text-sm font-semibold text-foreground tabular-nums mt-0.5 print:text-[9pt]">
                          {realisticUsd}
                        </p>
                      </div>
                    )}
                  </div>
                  {decisions.length > 0 && (
                    <div className="flex items-center gap-2">
                      {decisions.map((d) => (
                        <ActionChip
                          key={d.tier}
                          value={d.tier}
                          label={`${d.count} ${d.label}`}
                          className="tabular-nums"
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {hasCopPrograms && !(priorityOverrides && priorityOverrides.length > 0) && (
          <p className="mt-5 text-[11px] text-muted-foreground print:text-[7pt] print:mt-3 max-w-3xl">
            Funding Potential sums numeric programme ceilings in USD; COP amounts use COP ×{' '}
            {REALISTIC_FUNDING_DECK_FX.USD_PER_COP} (approximate deck rate, not for accounting).
          </p>
        )}
        {priorityOverrides && priorityOverrides.length > 0 && (
          <p className="mt-5 text-[11px] text-muted-foreground print:text-[7pt] print:mt-3 max-w-3xl">
            &ldquo;Realistic funding (est.)&rdquo; sums the same curated per-program ranges as Top Priority
            Programs for each objective, converted to USD (€ × {REALISTIC_FUNDING_DECK_FX.USD_PER_EUR},{' '}
            PLN × {REALISTIC_FUNDING_DECK_FX.USD_PER_PLN}, COP ×{' '}
            {REALISTIC_FUNDING_DECK_FX.USD_PER_COP}) for a single band. It is
            not additive with Funding Potential, which sums program ceilings in USD (COP ceilings ×{' '}
            {REALISTIC_FUNDING_DECK_FX.USD_PER_COP}) where numeric max is available.
          </p>
        )}
      </div>
    </Slide>
  );
}
